import { PermissionsHelper } from './Permission';
import localStore from 'store';

export function isAuAgent(): boolean {
  const permission = new PermissionsHelper();
  const user = localStore.get('user') || {};

  const setting = permission.get('general_settings', 'au_flow_users', 'additional_data') || {};
  const users = setting.users || [];
  const roles = setting.roles || [];

  return users.includes(user.id) || roles.includes(user.role);
}

function hasStrValue(value: any): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isAuClient(client: any): boolean {
  if (!isAuAgent()) {
    return false;
  }

  if (!client) {
    return false;
  }

  return hasStrValue(client.campaign_source_au) || hasStrValue(client.original_campaign_source_au);
}

export function isAuProduct(product: string): boolean {
  return !!(product && product.toLowerCase().includes('australia'));
}

export const AU_ONLY_CAMPAIGN_SOURCE = 'MDC Australia Flow';

/**
 * A client sits in exactly one of three states:
 *  - 'ca_only'    no AU campaign source at all
 *  - 'au_only'    AU campaign source and campaign_source is the AU flow — no Canada side
 *  - 'ca_and_au'  AU campaign source alongside a regular Canada campaign source
 */
export type ClientRegionState = 'ca_only' | 'au_only' | 'ca_and_au';

/** AU markers on the entity itself, independent of the agent's own permissions. */
export function hasAuCampaignSource(client: any): boolean {
  if (!client) {
    return false;
  }

  return hasStrValue(client.campaign_source_au) || hasStrValue(client.original_campaign_source_au);
}

export function getClientRegionState(client: any): ClientRegionState {
  if (!hasAuCampaignSource(client)) {
    return 'ca_only';
  }

  const campaignSource = (client && client.campaign_source) || '';

  return campaignSource === AU_ONLY_CAMPAIGN_SOURCE ? 'au_only' : 'ca_and_au';
}

/** Australia-only: no Canada side, so no region switcher and everything bills as AUMG. */
export function isAuOnlyClient(client: any): boolean {
  return getClientRegionState(client) === 'au_only';
}

/** Both regions: this is the state that earns a Canada/Australia switcher. */
export function isCaAndAuClient(client: any): boolean {
  return getClientRegionState(client) === 'ca_and_au';
}

/**
 * The Canada/Australia switcher only makes sense for dual-region clients, and stays
 * gated on the agent being part of the AU flow. Australia-only clients have nothing
 * to switch to, so they get the AU treatment outright rather than a switcher.
 */
export function canSwitchClientRegion(client: any): boolean {
  return isAuAgent() && isCaAndAuClient(client);
}
